import React, { useState, useEffect } from 'react';
import Menu from './Menu';
import { COUNTRIES, findCountry, withPinned, isoToFlagEmoji } from './country-data';
// import '../../assets/styles/auth-tokens.css';
// import '../../assets/styles/phone-input.css';

// Parses a leading combined "<dialCode><number>" string (the format this
// component emits) back into a country + national number, longest dial
// code match wins so e.g. "1" (US/Canada) doesn't shadow "1264" (Anguilla).
const parseValue = (value, fallbackCountry) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return { country: fallbackCountry, number: '' };

  const match = COUNTRIES
    .filter((c) => digits.startsWith(c.dialCode))
    .sort((a, b) => b.dialCode.length - a.dialCode.length)[0];

  if (!match) return { country: fallbackCountry, number: digits };
  return { country: match, number: digits.slice(match.dialCode.length) };
};

const countryTrigger = (country) => ({ toggle }) => (
  <button
                type="button"
                className="flex items-center px-4 py-[11px] h-full border-r border-slate-200 text-sm hover:bg-slate-50 transition-colors rounded-l-xl outline-none shrink-0"
                onClick={toggle}
                aria-label="Select country code"
              >
                <span className="mr-2 text-lg leading-none">{isoToFlagEmoji(country.iso2)}</span>
                <span className="text-slate-700 font-bold mr-1">+{country.dialCode}</span>
                <i className="icofont-simple-down text-slate-400 text-xs ml-1"></i>
              </button>
);

const PhoneNumberInput = ({
  value,
  onChange,
  defaultCountry = 'zw',
  placeholder = 'Phone number',
  name,
  required,
  className = '',
  label,
  error,
  hint,
  containerClassName = 'w-full',
}) => {
  const fallbackCountry = findCountry(defaultCountry) || COUNTRIES[0];
  const initial = parseValue(value, fallbackCountry);

  const [country, setCountry] = useState(initial.country);
  const [number, setNumber] = useState(initial.number);

  // Sync with external value changes (like auto-fill)
  useEffect(() => {
    if (value !== undefined && value !== null) {
      const parsed = parseValue(value, fallbackCountry);
      if (parsed.number !== number || parsed.country.iso2 !== country.iso2) {
        setCountry(parsed.country);
        setNumber(parsed.number);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const countryItems = withPinned(country.iso2);

  const emitChange = (nextCountry, nextNumber) => {
    onChange?.(nextCountry.dialCode + nextNumber);
  };

  const handleNumberChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    setNumber(digits);
    emitChange(country, digits);
  };

  const handleCountrySelect = (c) => {
    setCountry(c);
    emitChange(c, number);
  };

  return (
    <div className={containerClassName}>
      {label && (
        <label className="label py-1">
          <span className="label-text text-xs font-bold text-slate-700">{label}</span>
        </label>
      )}
      <div className={`flex items-stretch w-full rounded-xl bg-white border ${error ? 'border-error focus-within:border-error focus-within:ring-error/20' : 'border-slate-300 focus-within:border-[#135A9C] focus-within:ring-[#135A9C]/20'} focus-within:ring-2 transition-all h-[3rem] ${className}`}>
        <Menu
          className="h-full flex items-stretch"
          trigger={countryTrigger(country)}
          items={countryItems}
          getKey={(c) => c.iso2}
          searchable
          searchPlaceholder="Search country or code"
          filterItem={(c, q) => {
            const query = q.toLowerCase().replace('+', '');
            return c.name.toLowerCase().includes(query) || c.dialCode.includes(query);
          }}
          renderItem={(c) => (
            <span className="flex items-center w-full">
              <span className="mr-3 text-lg leading-none">{isoToFlagEmoji(c.iso2)}</span>
              <span className="text-slate-700 truncate flex-1 mr-2">{c.name}</span>
              <span className="text-slate-400 font-medium">+{c.dialCode}</span>
            </span>
          )}
          onSelect={handleCountrySelect}
        />
        <input
          type="tel"
          inputMode="numeric"
          className="flex-1 px-4 py-[11px] bg-transparent outline-none text-sm w-full h-full text-slate-800 placeholder-slate-400 rounded-r-xl"
          value={number}
          onChange={handleNumberChange}
          placeholder={placeholder}
          name={name}
          required={required}
        />
      </div>
      {hint && !error && (
        <label className="label py-1">
          <span className="label-text-alt text-slate-500">{hint}</span>
        </label>
      )}
      {error && <p className="text-error text-xs mt-1">{error}</p>}
    </div>
  );
};

export default PhoneNumberInput;
